import { ReactNode, useContext } from 'react';
import { Tooltip } from '@mui/material';
import type { TooltipProps } from '@mui/material/Tooltip';
import { AiChatVariantContext } from './context';
import { ActionBtn } from './styles';

type Props = {
  /** hover 툴팁 문구 */
  tooltip: string;
  onClick: () => void;
  disabled?: boolean;
  placement?: TooltipProps['placement'];
  /** 버튼 아이콘 */
  children: ReactNode;
  'aria-label'?: string;
};

/**
 * AI Chat 답변 하단 액션 버튼 — 회의록/파일 뷰어가 공유.
 * 버튼 껍데기 + 툴팁을 한 컴포넌트로 묶고, 아이콘/문구/동작만 주입받는다.
 * 툴팁 색은 테마(context)에 따라 자동 반전(light=검정, dark=흰색)된다.
 */
const AiChatActionButton = ({
  tooltip,
  onClick,
  disabled,
  // 버튼 위-왼쪽 기준으로 붙이고 꼬리(arrow)는 하단에 오게 한다
  placement = 'top-start',
  children,
  'aria-label': ariaLabel,
}: Props) => {
  const variant = useContext(AiChatVariantContext);
  const isDark = variant === 'dark';
  const tipBg = isDark ? '#FFFFFF' : '#000000';
  const tipColor = isDark ? '#2B2D2F' : '#FFFFFF';

  return (
    <Tooltip
      title={tooltip}
      placement={placement}
      arrow
      // 툴팁은 Portal 로 렌더되므로 CSS 변수 대신 sx 로 직접 색 지정
      slotProps={{
        // 버튼에 바짝 붙게 (기본 간격 제거)
        popper: {
          modifiers: [{ name: 'offset', options: { offset: [0, 0] } }],
        },
        tooltip: {
          sx: {
            bgcolor: tipBg,
            color: tipColor,
            fontSize: 14,
            fontWeight: 400,
            lineHeight: '20px',
            padding: '4px 8px',
            borderRadius: '4px',
            whiteSpace: 'nowrap',
            maxWidth: 'none',
          },
        },
        arrow: { sx: { color: tipBg } },
      }}
    >
      {/* disabled 버튼도 툴팁이 뜨도록 span 으로 감싼다 */}
      <span style={{ display: 'inline-flex' }}>
        <ActionBtn
          type="button"
          aria-label={ariaLabel ?? tooltip}
          onClick={onClick}
          disabled={disabled}
        >
          {children}
        </ActionBtn>
      </span>
    </Tooltip>
  );
};

export default AiChatActionButton;
